import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { assetApi } from '../../api/asset.api';
import { Category } from 'api/category.api';
import { categoryApi } from '../../api/category.api';
import { saveAs } from 'file-saver';
import OptimizedImage from '../common/OptimizedImage';

interface Asset {
  id: number;
  name: string;
  description: string;
  categoryId: number;
  categoryName: string;
  warrantyPeriod: number;
  assignedToUserName: string | null;
  status: string;
  imageUrl?: string;
}

const AssetList: React.FC = () => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(0);
  const [limit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const [categoryId, setCategoryId] = useState<number | undefined>();
  const [status, setStatus] = useState<string | undefined>();
  const [categories, setCategories] = useState<Category[]>([]);
  const [exportLoading, setExportLoading] = useState(false);

  const [updatingIds, setUpdatingIds] = useState<Set<number>>(new Set());

  const navigate = useNavigate();
  const location = useLocation();

  const [isAnimating, setIsAnimating] = useState(false);
  const [pageDirection, setPageDirection] = useState<'next' | 'prev'>('next');

  // map some URL filter strings to status values (optional)
//   const FILTER_TO_STATUS: Record<string, string> = {
//     ACTIVE: 'AVAILABLE',
//     INACTIVE: 'INACTIVE',
//     STOLEN: 'STOLEN',
//     DISPOSED: 'DISPOSED',
//   };

  // Normalize URL filter: e.g. expiring-soon -> EXPIRING_SOON
  const urlFilter = useMemo(() => {
    const qp = new URLSearchParams(location.search);
    const raw = qp.get('filter');
    if (!raw) return undefined;
    return raw.toUpperCase().replace(/-/g, '_');
  }, [location.search]);

  // If page loaded with filter, clear dropdown status so UI reflects applied filter
  useEffect(() => {
    if (urlFilter) {
      setStatus(undefined);
      setPage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlFilter]);

  // Reset page when category/status/urlFilter changes
  useEffect(() => {
    setPage(0);
  }, [categoryId, status, urlFilter]);

  // Fetch categories once
  const fetchCategories = useCallback(async () => {
    try {
      const response = await categoryApi.getCategories();
      setCategories(response);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to fetch categories');
    }
  }, []);

  // Main fetchData: send filter OR status (not both). Use defensive reads.
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {
        page,
        limit,
        categoryId,
        // include filter always (backend may treat null/undefined)
        filter: urlFilter,
        // include dropdown status only when no urlFilter present
        ...(urlFilter ? {} : { status }),
      };

      const response = await assetApi.getAssets(params);
      setAssets(response?.data?.data ?? []);
      setTotalItems(response?.data?.total ?? 0);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to fetch assets');
    } finally {
      setLoading(false);
    }
  }, [page, limit, categoryId, status, urlFilter]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const blob = await assetApi.exportAssetAssignmentHistory(undefined, categoryId);
      const fileName = `asset_assignment_history_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(blob, fileName);
    } catch (err: any) {
      console.error('Export failed:', err);
      alert('Failed to export data. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  const getExportTooltip = () => {
    if (categoryId) {
      const selectedCategory = categories.find(cat => cat.id === categoryId);
      return `Export assignment history for ${selectedCategory?.name || 'selected category'}`;
    }
    return 'Export all asset assignment history';
  };

  // Update asset status (activate/inactivate). After success: remove "filter" from URL and refresh.
  const handleStatusToggle = async (id: number, currentStatus: string) => {
    const isInactive = currentStatus === 'INACTIVE';
    const verb = isInactive ? 'activate' : 'inactivate';

    if (!window.confirm(`Are you sure you want to ${verb} this asset?`)) return;

    // mark updating
    setUpdatingIds(prev => {
      const copy = new Set(prev);
      copy.add(id);
      return copy;
    });

    try {
      const apiResp = isInactive ? await assetApi.activeAsset(id) : await assetApi.inactiveAsset(id);
      // if your API returns { success: false } you can handle it here
      if (apiResp && apiResp.success === false) {
        alert(apiResp.message || 'Operation failed on server');
        return;
      }

      // If URL contains filter, remove it so updated asset shows in list
     // ... after successful API call and apiResp check:
const params = new URLSearchParams(location.search);
if (params.has('filter')) {
  params.delete('filter');
  const newSearch = params.toString() ? `?${params.toString()}` : '';
  navigate({ pathname: location.pathname, search: newSearch }, { replace: true });
  // DO NOT await fetchData() here — rely on useEffect to pick up the new URL and refresh.
} else {
  // no filter in URL: just refresh current data
  await fetchData();
}

    } catch (err: any) {
      console.error(`Failed to ${verb} asset:`, err);
      alert(err?.response?.data?.message ?? 'Failed to update asset status.');
    } finally {
      setUpdatingIds(prev => {
        const copy = new Set(prev);
        copy.delete(id);
        return copy;
      });
    }
  };

  // New: Apply Filters handler — when user clicks Apply, we convert dropdown selection into URL params
  // If a status is selected, we remove filter and set status param; otherwise we remove filter and set categoryId only.
  const handleApplyFilters = useCallback(() => {
    const params = new URLSearchParams(location.search);
  
    if (status) {
      // remove URL filter and set status param so backend will use status
      params.delete('filter');
      params.set('status', status);
    } else {
      // no status: remove both filter and status (so backend can use category or default)
      params.delete('status');
      params.delete('filter');
    }
  
    if (categoryId) {
      params.set('categoryId', String(categoryId));
    } else {
      params.delete('categoryId');
    }
  
    // reset page to 0 when applying new filters
    params.set('page', '0');
  
    const newSearch = params.toString() ? `?${params.toString()}` : '';
    // navigate and let the useEffect(fetchData) run after location changes.
    navigate({ pathname: location.pathname, search: newSearch }, { replace: true });
  
    // DO NOT call fetchData() here — it's a race with location update.
  }, [location.search, status, categoryId, navigate, location.pathname]);
  

  const renderStatusBadge = (statusStr: string) => {
    const statusStyles: Record<string, string> = {
      AVAILABLE: 'bg-green-100 text-green-800',
      ASSIGNED: 'bg-blue-100 text-blue-800',
      INACTIVE: 'bg-red-100 text-red-800',
      STOLEN: 'bg-red-700 text-white',
      DISPOSED: 'bg-gray-500 text-white',
    };
    return (
      <span
        className={`px-2 inline-flex text-xs font-semibold rounded-full ${
          statusStyles[statusStr] || 'bg-gray-100 text-gray-800'
        }`}
      >
        {statusStr}
      </span>
    );
  };

  const headers = useMemo(() => ['Image', 'Asset Name', 'Category', 'Assigned User', 'Status', 'Actions'], []);
  const totalPages = Math.max(1, Math.ceil(totalItems / limit));

  const handlePageChange = (newPage: number) => {
    setPageDirection(newPage > page ? 'next' : 'prev');
    setIsAnimating(true);
    setTimeout(() => {
      setPage(newPage);
      setIsAnimating(false);
    }, 300);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Asset Management</h1>
        <div className="flex space-x-3">
          <button
            onClick={handleExport}
            disabled={exportLoading}
            title={getExportTooltip()}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition disabled:opacity-50 flex items-center space-x-2 relative group"
          >
            {exportLoading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Exporting...</span>
              </>
            ) : (
              <>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Export Assignment History</span>
              </>
            )}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              {getExportTooltip()}
            </div>
          </button>

          <Link to="/assets/add" className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark transition">
            Add New Asset
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex space-x-4">
        <select
          value={categoryId ?? ''}
          onChange={(e) => { setCategoryId(e.target.value ? Number(e.target.value) : undefined); setPage(0); }}
          className="border rounded px-3 py-2"
        >
          <option value="">All Categories</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ))}
        </select>

        <select
          value={status ?? ''}
          onChange={(e) => { setStatus(e.target.value || undefined); setPage(0); }}
          className="border rounded px-3 py-2"
        >
          <option value="">All Statuses</option>
          <option value="AVAILABLE">Available</option>
          <option value="ASSIGNED">Assigned</option>
          <option value="INACTIVE">Inactive</option>
          <option value="STOLEN">Stolen</option>
          <option value="DISPOSED">Disposed</option>
        </select>

        <button onClick={handleApplyFilters} className="bg-blue-500 text-white px-4 py-2 rounded">
          Apply Filters
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">Loading...</div>
      ) : error ? (
        <div className="text-red-600 p-4">Error: {error}</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-primary">
              <tr>
                {headers.map((header) => (
                  <th
                    key={header}
                    className="px-6 py-3 text-left text-xs font-bold text-slate-100  uppercase tracking-wider"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody
              className={`bg-white divide-y divide-gray-200 transition-all duration-300 ${
                isAnimating
                  ? pageDirection === 'next'
                    ? 'opacity-0 -translate-y-4 pointer-events-none'
                    : 'opacity-0 translate-y-4 pointer-events-none'
                  : 'opacity-100 translate-y-0'
              }`}
            >
              {assets.length === 0 ? (
                <tr>
                  <td colSpan={headers.length} className="px-6 py-4 text-center text-gray-500">
                    No assets found.
                  </td>
                </tr>
              ) : (
                assets.map((asset) => {
                  const toggleLabel =
                    asset.status === 'INACTIVE'
                      ? 'Activate'
                      : asset.status === 'AVAILABLE'
                      ? 'Inactivate'
                      : null;

                  const toggleClass =
                    asset.status === 'INACTIVE'
                      ? 'text-green-600 hover:text-green-900'
                      : 'text-red-600 hover:text-red-900';

                  return (
                    <tr key={asset.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {asset.imageUrl ? (
                          <OptimizedImage 
                            src={asset.imageUrl} 
                            alt={asset.name} 
                            size="thumbnail"
                            width={48}
                            height={56}
                            className="rounded"
                          />
                        ) : (
                          <span className="text-gray-400 text-sm">No Image</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{asset.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{asset.categoryName}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {asset.assignedToUserName || <span className="text-gray-500">Unassigned</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{renderStatusBadge(asset.status)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => navigate(`/assets/${asset.id}`)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View
                        </button>
                        <button
                          onClick={() => navigate(`/assets/edit/${asset.id}`)}
                          className="text-yellow-600 hover:text-yellow-900"
                        >
                          Edit
                        </button>
                        {toggleLabel && (
                          <button
                            onClick={() => handleStatusToggle(asset.id, asset.status)}
                            className={`${toggleClass} ${updatingIds.has(asset.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={updatingIds.has(asset.id)}
                          >
                            {updatingIds.has(asset.id) ? 'Updating...' : toggleLabel}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="flex justify-end mt-4 space-x-2">
        <button
          disabled={page === 0 || isAnimating}
          onClick={() => handlePageChange(Math.max(page - 1, 0))}
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          Prev
        </button>
        <span className="px-3 py-1">Page {page + 1} of {totalPages}</span>
        <button
          disabled={page + 1 >= totalPages || isAnimating}
          onClick={() => handlePageChange(page + 1)}
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default AssetList;
